/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'

import clsx from 'clsx'

import FileUpload from './fileUpload'
import EmojiSender from './emojiSender'
import VoiceRecorder from './voiceRecorder'
import { useDispatch, useSelector } from 'react-redux'
import { sendFileMessage, setReplyingMessage } from 'app/redux/messages/messages.actions'
import { SendIcon, CheckIcon } from 'app/components/icons'
import UploadHints from './UploadHints'
import UploadConfirm from './UploadConfirm'
import usePasteHandler from 'app/hooks/usePasteHandler'
import './chatInput.scss'

const getUserInput = ({ messagesData }) => messagesData.messages.userInput
const getVoiceRecording = ({ messagesData }) => messagesData.messages.voiceRecording
const getDisableInput = ({ messagesData }) => messagesData.messages.disableInput
const getSocketConnectionError = ({ messagesData }) => messagesData.messages.socketConnectionError
const getSendVoiceMsgEnabled = ({ widgetData }) => widgetData.widgetInfo.data.publicSetting.sendVoiceWidgetEnabled
const getSendFileMsgEnabled = ({ widgetData }) => widgetData.widgetInfo.data.publicSetting.sendFileEnabled
const getLanguageInfo = ({ widgetData }) => widgetData.widgetInfo.languageInfo
const getActiveDepartment = ({ messagesData }) => messagesData.activeDepartment.data
const getReplyingMessage = ({ messagesData }) => messagesData.replyingMessage
const getRecordingAudio = ({ messagesData }) => messagesData.messages.recordingAudio
const getUploadConfirmData = ({ messagesData }) => messagesData.messages.confirmUploadedData

const ChatInput = React.forwardRef(({ toggleIcons, onChange, onKeyDown, showIcons, disabled }, ref) => {
  const [inputFocused, setInputFocused] = useState(false)
  const userInput = useSelector(getUserInput)
  const voiceRecording = useSelector(getVoiceRecording)
  const disableInput = useSelector(getDisableInput)
  const socketConnectionError = useSelector(getSocketConnectionError)
  const isSendVoiceEnabled = useSelector(getSendVoiceMsgEnabled)
  const languageInfo = useSelector(getLanguageInfo)
  const activeDepartment = useSelector(getActiveDepartment)
  const replyingMessage = useSelector(getReplyingMessage)
  const recordingAudio = useSelector(getRecordingAudio)
  const isSendFileMsgEnabled = useSelector(getSendFileMsgEnabled)
  const confirmUploadedData = useSelector(getUploadConfirmData)

  // send on file paste
  usePasteHandler((file) => dispatch(sendFileMessage(file)))

  const dispatch = useDispatch()

  const userInputValue = userInput[activeDepartment] || ''
  const isSendBtnActive = !disabled && (!!userInputValue?.trim() || recordingAudio)
  const SubmitIcon = recordingAudio ? CheckIcon : SendIcon

  const handleInputKeyDown = (e) => {
    // pressing Enter with empty input
    if ((!e || (e && e.key === 'Enter' && !e.shiftKey)) && userInputValue.trim() === '') {
      e.preventDefault()
    } else {
      onKeyDown(e)
    }
  }

  const handleSendClick = () => {
    if (disabled) return

    if (recordingAudio) {
      // Components and logics were in a seperate component - decoupling those was a nightmare -- so i just simulated a click on the correct button.
      const sendVoiceElement = document.querySelector('#send_voice_icon')

      sendVoiceElement?.click()
    } else {
      onKeyDown()
      dispatch(setReplyingMessage(null))
    }
  }

  const handleChangeInputSize = () => {
    if (ref.current) {
      ref.current.style.height = 'auto' // Reset height to auto to calculate the actual height needed
      ref.current.style.height = `${ref.current.scrollHeight - 3}px` // 3px is to prevent textarea height change when starting to type
    }
  }

  const handleInputChange = (e) => {
    onChange(e)
    handleChangeInputSize()
  }

  const handleFocusinput = () => setInputFocused(true)
  const handleBlurFocus = () => setInputFocused(false)

  const textInput = (
    <textarea
      type="text"
      value={userInputValue}
      className="chat_input"
      placeholder={languageInfo.texts.typeHere}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onFocus={handleFocusinput}
      onBlur={handleBlurFocus}
      rows={1}
      ref={ref}
      id="user_input"
      dir="auto"
    />
  )

  const renderFileUploadBtn = isSendFileMsgEnabled && <FileUpload />

  const renderTextModeComponents = () => {
    if (voiceRecording) return <></>

    return (
      <>
        {textInput}
        <EmojiSender toggleIcons={toggleIcons} {...{ showIcons }} />
        {renderFileUploadBtn}
      </>
    )
  }

  // different order for each direction
  const renderChatInputComponents = () => {
    return (
      <div className="chat_input_wrapper">
        <div
          data-focused={inputFocused}
          className={clsx(
            'chat_input_stuff',
            recordingAudio ? 'chat_input_stuff_recording_active' : 'chat_input_stuff_recording_inactive',
          )}
        >
          <div className={clsx('faded_out_icon_container', !userInputValue && isSendVoiceEnabled && 'fade_in')}>
            {isSendVoiceEnabled && <VoiceRecorder />}
          </div>
          {renderTextModeComponents()}
        </div>

        <button
          disabled={disabled}
          className={clsx('chat_send_btn', isSendBtnActive ? 'chat_send_btn_active' : 'chat_send_btn_inactive')}
          onClick={handleSendClick}
        >
          <SubmitIcon
            className={clsx(
              'send_message_icon',
              isSendBtnActive ? 'send_message_icon_active' : 'send_message_icon_inactive',
              !recordingAudio && 'send_text_btn',
            )}
            color="#fff"
          />
        </button>
      </div>
    )
  }

  const renderChatInputBar = () => {
    const shouldDisableChat = disableInput.status || socketConnectionError.status
    const disableChatText = disableInput.description || socketConnectionError.description

    // content when error or notif should display
    if (shouldDisableChat) {
      return <span className="input_message raychat-font-bold">{disableChatText}</span>
    }

    return renderChatInputComponents()
  }

  useEffect(() => {
    if (replyingMessage) {
      ref.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyingMessage])

  useEffect(() => {
    if (userInputValue !== undefined && userInputValue === '' && ref.current) {
      ref.current.style.height = 'auto'
      handleChangeInputSize()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInputValue])

  return (
    <div className="chat_input_container_wrapper">
      <UploadHints />
      {confirmUploadedData && <UploadConfirm />}
      {!confirmUploadedData && !disableInput.status && (
        <div className="chat_input_container">{renderChatInputBar()}</div>
      )}
    </div>
  )
})

export default React.memo(ChatInput)
